// Het vlak Gezondheid: het gezondheidsbord (spoor B, B7; meterlijst v3, maquette
// docs/input/frontend/gezondheidsbord/gezondheidsbord.html).
//
// Drie lagen. Laag 1: per propositie vijf vakjes (Bronnen · Trends · Skills ·
// Kansen · Urgentie), elk met zijn kernmeter, en ICT als ondergrens eronder.
// Laag 2: de meters van het gekozen vakje, met getal, ICT, doel en stand.
// Laag 3: een paneel met de feiten, het verloop en de drie slechtste
// voorbeelden (GezondheidPaneel.tsx). Bovenaan: meest gestegen, meest gezakt
// en de knop Voorwaarden; daarachter staan ook de lanen, de wachters en de
// stille nul die vroeger dit hele vlak waren (GezondheidDraait.tsx).
//
// Lat, stand en woord komen van de server (GET /beheer/gezondheid/bord). Dit
// scherm rekent niets na: het kiest alleen de kleur en de volgorde. Het bord
// opent op het grootste gat, zoals de server het aanwijst.

import { useState, type CSSProperties } from "react";

import { Card, SlidePanel, TableHead } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type {
  GezondheidsbordBeweging,
  GezondheidsbordCel,
  GezondheidsbordKleur,
  GezondheidsbordMeter,
  GezondheidsbordResponse,
} from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import { GezondheidInhoud } from "./GezondheidInhoud";
import { MeterPaneel, VoorwaardenPaneel, Zin } from "./GezondheidPaneel";
import { useSmal } from "./Overzicht";
import { KLEUR, Pil, cijfer, getal, kop, kort, maand } from "./GezondheidStukken";

type Vakje = GezondheidsbordResponse["vakjes"][number];
type Sector = GezondheidsbordResponse["sectoren"][number];
type Keuze = { sector: string; vak: Vakje["code"] };
type Paneel =
  | { soort: "meter"; code: string; sector: string | null; terug: boolean }
  | { soort: "voorwaarden" };

export function Gezondheid() {
  const bord = useResource<GezondheidsbordResponse>(() => api.gezondheidsbord(), []);
  const [keuze, setKeuze] = useState<Keuze | null>(null);
  const [paneel, setPaneel] = useState<Paneel | null>(null);

  if (bord.error) {
    return (
      <Card style={{ padding: 18 }}>
        <LoadError reason={bord.error} onRetry={bord.reload} />
      </Card>
    );
  }
  if (!bord.data) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason="het gezondheidsbord wordt opgehaald" />
      </Card>
    );
  }

  const b = bord.data;
  const pilots = b.sectoren.filter((s) => !s.ondergrens);
  const gat = b.strook.grootsteGat;
  const standaard: Keuze | null = gat
    ? { sector: gat.sector, vak: gat.vak as Vakje["code"] }
    : pilots[0]
      ? { sector: pilots[0].sleutel, vak: b.vakjes[0].code }
      : null;
  const gekozen = keuze ?? standaard;
  const opGat = !!gat && gekozen?.sector === gat.sector && gekozen.vak === gat.vak;

  const sluit = () =>
    setPaneel(paneel?.soort === "meter" && paneel.terug ? { soort: "voorwaarden" } : null);
  const paneelMeter =
    paneel?.soort === "meter" ? b.meters.find((m) => m.code === paneel.code) : undefined;
  const paneelSector =
    paneel?.soort === "meter" && paneel.sector
      ? (b.sectoren.find((s) => s.sleutel === paneel.sector) ?? null)
      : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Kop bord={b} />

      <Strook
        bord={b}
        onBeweging={(bw) => {
          setKeuze({ sector: bw.sector, vak: bw.vak as Vakje["code"] });
          setPaneel({ soort: "meter", code: bw.meter, sector: bw.sector, terug: false });
        }}
        onVoorwaarden={() => setPaneel({ soort: "voorwaarden" })}
      />

      {b.inhoudstabel ? <GezondheidInhoud tabel={b.inhoudstabel} sectoren={b.sectoren} /> : null}

      <section>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {/* Op een smal scherm schuift de tabel binnen zijn kaart, niet de pagina. */}
          <div style={{ overflowX: "auto", position: "relative" }}>
            <table
              aria-label="Vijf vakjes per propositie"
              style={{ width: "100%", minWidth: 720, borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th scope="col" style={{ ...kop, minWidth: 150 }}>
                    Propositie
                  </th>
                  {b.vakjes.map((v) => {
                    const kern = b.meters.find((m) => m.code === v.kernmeter);
                    return (
                      <th key={v.code} scope="col" style={{ ...kop, minWidth: 112 }} title={v.vraag}>
                        {v.naam}
                        <span
                          style={{
                            display: "block",
                            marginTop: 3,
                            textTransform: "none",
                            letterSpacing: 0,
                            fontWeight: weight.regular,
                            fontSize: size.label,
                          }}
                        >
                          {kern ? `${kern.code} ${kern.naam}` : v.kernmeter}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {b.sectoren.map((s) => (
                  <BordRij
                    key={s.sleutel}
                    sector={s}
                    bord={b}
                    gekozen={gekozen}
                    onKies={(vak) => setKeuze({ sector: s.sleutel, vak })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Legenda />
      </section>

      {gekozen && (
        <Laag2
          bord={b}
          keuze={gekozen}
          opGat={opGat}
          onMeter={(code) =>
            setPaneel({ soort: "meter", code, sector: gekozen.sector, terug: false })
          }
        />
      )}

      <SlidePanel open={paneel !== null} onClose={sluit}>
        {paneel?.soort === "voorwaarden" && (
          <VoorwaardenPaneel
            bord={b}
            onClose={sluit}
            onKiesMeter={(code) => setPaneel({ soort: "meter", code, sector: null, terug: true })}
          />
        )}
        {paneelMeter && (
          <MeterPaneel meter={paneelMeter} sector={paneelSector} bord={b} onClose={sluit} />
        )}
      </SlidePanel>
    </div>
  );
}

/** Waar de getallen vandaan komen en welke lat er geldt — één keer, bovenaan. */
function Kop({ bord }: { bord: GezondheidsbordResponse }) {
  const periode = maand(bord.periode);
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {bord.bron !== "metingen" && (
        <div
          data-bron={bord.bron}
          style={{
            background: color.surfaceQuote,
            border: `1px solid ${color.lineFaint}`,
            borderRadius: radius.quote,
            padding: "10px 14px",
            fontSize: size.meta,
            color: color.body,
            lineHeight: 1.5,
          }}
        >
          {bord.bronTekst}
        </div>
      )}
      <p style={{ margin: 0, fontSize: 12, color: color.faint, lineHeight: 1.5 }}>
        {[periode ? `Maand: ${periode}` : null, bord.latRegel]
          .filter(Boolean)
          .join(" · ")}
      </p>
    </div>
  );
}

function Strook({
  bord,
  onBeweging,
  onVoorwaarden,
}: {
  bord: GezondheidsbordResponse;
  onBeweging: (b: GezondheidsbordBeweging) => void;
  onVoorwaarden: () => void;
}) {
  const { gestegen, gezakt, bewegingReden, voorwaardenRood, voorwaardenGeteld } = bord.strook;
  const tegel = {
    background: color.surface,
    border: `1px solid ${color.line}`,
    borderRadius: radius.block,
    padding: "11px 14px",
    display: "grid",
    gap: 3,
    minWidth: 0,
    textAlign: "left",
    font: "inherit",
    color: "inherit",
  } as const;
  const vw =
    voorwaardenGeteld === 0
      ? { tekst: "nog niets geteld · bekijk", kleur: color.soft }
      : voorwaardenRood > 0
        ? { tekst: `${voorwaardenRood} van ${voorwaardenGeteld} niet in orde · bekijk`, kleur: color.accentRed }
        : { tekst: `alle ${voorwaardenGeteld} in orde · bekijk`, kleur: color.accentGreen };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        gap: 10,
      }}
    >
      <Beweging
        label="Meest gestegen"
        beweging={gestegen}
        reden={bewegingReden}
        bord={bord}
        stijl={tegel}
        onKies={onBeweging}
      />
      <Beweging
        label="Meest gezakt"
        beweging={gezakt}
        reden={bewegingReden}
        bord={bord}
        stijl={tegel}
        onKies={onBeweging}
      />
      <button
        type="button"
        onClick={onVoorwaarden}
        data-knop="voorwaarden"
        style={{ ...tegel, cursor: "pointer" }}
      >
        <span style={{ ...sectionLabel, marginBottom: 0 }}>Voorwaarden</span>
        <span style={{ fontSize: size.body, fontWeight: weight.medium, color: vw.kleur }}>
          {vw.tekst}
        </span>
      </button>
    </div>
  );
}

function Beweging({
  label,
  beweging,
  reden,
  bord,
  stijl,
  onKies,
}: {
  label: string;
  beweging: GezondheidsbordBeweging | null;
  reden: string | null;
  bord: GezondheidsbordResponse;
  stijl: CSSProperties;
  onKies: (b: GezondheidsbordBeweging) => void;
}) {
  if (!beweging) {
    return (
      <div style={stijl}>
        <span style={{ ...sectionLabel, marginBottom: 0 }}>{label}</span>
        <span style={{ fontSize: size.meta, color: color.soft, lineHeight: 1.45 }}>
          {reden ?? "Niets bewogen sinds de vorige meting."}
        </span>
      </div>
    );
  }
  const sector = bord.sectoren.find((s) => s.sleutel === beweging.sector);
  const vak = bord.vakjes.find((v) => v.code === beweging.vak);
  return (
    <button type="button" onClick={() => onKies(beweging)} style={{ ...stijl, cursor: "pointer" }}>
      <span style={{ ...sectionLabel, marginBottom: 0 }}>{label}</span>
      <span style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink }}>
        {beweging.naam}
      </span>
      <span style={{ fontSize: size.meta, color: color.soft }}>
        {`${kort(sector?.naam ?? beweging.sector)} · ${vak?.naam ?? beweging.vak} · `}
        <span style={cijfer}>
          {`${getal(beweging.vorige, beweging.eenheid)} → ${getal(beweging.waarde, beweging.eenheid)}`}
        </span>
      </span>
    </button>
  );
}

function BordRij({
  sector,
  bord,
  gekozen,
  onKies,
}: {
  sector: Sector;
  bord: GezondheidsbordResponse;
  gekozen: Keuze | null;
  onKies: (vak: Vakje["code"]) => void;
}) {
  const td = { padding: "6px 6px", borderTop: `1px solid ${color.lineFaint}` } as const;
  return (
    <tr data-sector={sector.sleutel}>
      <th
        scope="row"
        style={{
          ...td,
          padding: "8px 12px",
          textAlign: "left",
          fontSize: size.meta,
          fontWeight: weight.medium,
          color: color.ink,
        }}
      >
        {sector.naam}
        {sector.ondergrens && (
          <span
            style={{
              display: "block",
              fontSize: size.label,
              fontWeight: weight.regular,
              color: color.faint,
              marginTop: 2,
            }}
          >
            ondergrens, de lat staat eronder
          </span>
        )}
      </th>
      {bord.vakjes.map((v) => {
        const kern = bord.meters.find((m) => m.code === v.kernmeter);
        const cel = kern?.per[sector.sleutel];
        if (!kern || !cel) {
          return (
            <td key={v.code} style={td}>
              <EmptyReason reason="geen kernmeter" />
            </td>
          );
        }
        if (sector.ondergrens) {
          return (
            <td key={v.code} style={td}>
              <div
                data-vak={v.code}
                style={{
                  background: color.surfaceAlt,
                  color: color.soft,
                  borderRadius: radius.input,
                  padding: "9px 11px",
                }}
              >
                <span style={{ ...cijfer, display: "block", fontSize: 18, fontWeight: weight.bold }}>
                  {getal(cel.waarde, kern.eenheid)}
                </span>
                <span style={{ fontSize: size.label }}>{`lat ${kern.doelTekst}`}</span>
              </div>
            </td>
          );
        }
        const actief = gekozen?.sector === sector.sleutel && gekozen.vak === v.code;
        return (
          <td key={v.code} style={td}>
            <Vakknop
              cel={cel}
              meter={kern}
              actief={actief}
              vak={v.code}
              label={`${sector.naam}, ${v.naam}: ${getal(cel.waarde, kern.eenheid)}, ${cel.standWoord}${cel.reden ? ` (${cel.reden})` : ""}`}
              onKies={() => onKies(v.code)}
            />
          </td>
        );
      })}
    </tr>
  );
}

function Vakknop({
  cel,
  meter,
  actief,
  vak,
  label,
  onKies,
}: {
  cel: GezondheidsbordCel;
  meter: GezondheidsbordMeter;
  actief: boolean;
  vak: string;
  label: string;
  onKies: () => void;
}) {
  const k = KLEUR[cel.stand];
  return (
    <button
      type="button"
      data-vak={vak}
      data-kleur={cel.stand}
      aria-pressed={actief}
      aria-label={label}
      title={cel.reden ?? undefined}
      onClick={onKies}
      style={{
        width: "100%",
        textAlign: "left",
        background: k.vlak,
        color: k.tekst,
        border: "none",
        borderRadius: radius.input,
        padding: "9px 11px",
        font: "inherit",
        cursor: "pointer",
        boxShadow: actief ? `inset 0 0 0 2px ${color.ink}` : "none",
      }}
    >
      <span style={{ ...cijfer, display: "block", fontSize: 18, fontWeight: weight.bold }}>
        {getal(cel.waarde, meter.eenheid)}
      </span>
      <span style={{ fontSize: size.label, fontWeight: weight.medium }}>{cel.standWoord}</span>
    </button>
  );
}

function Legenda() {
  const items: [GezondheidsbordKleur, string][] = [
    ["groen", "boven het doel, of in de band"],
    ["oranje", "boven ICT, onder het doel"],
    ["rood", "onder ICT, leeg, of voorwaarde niet in orde"],
    ["grijs", "nog niet geteld of nog te bouwen"],
  ];
  return (
    <div
      aria-label="Legenda"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 18px",
        marginTop: 10,
        fontSize: 12,
        color: color.soft,
      }}
    >
      {items.map(([k, tekst]) => (
        <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            aria-hidden
            style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              background: KLEUR[k].vlak,
              boxShadow: `inset 0 0 0 1px ${KLEUR[k].tekst}`,
            }}
          />
          {tekst}
        </span>
      ))}
    </div>
  );
}

const RIJ = "minmax(220px,2.6fr) 84px 84px minmax(110px,1fr) 150px";

function Laag2({
  bord,
  keuze,
  opGat,
  onMeter,
}: {
  bord: GezondheidsbordResponse;
  keuze: Keuze;
  opGat: boolean;
  onMeter: (code: string) => void;
}) {
  const vak = bord.vakjes.find((v) => v.code === keuze.vak);
  const sector = bord.sectoren.find((s) => s.sleutel === keuze.sector);
  const ict = bord.sectoren.find((s) => s.ondergrens);
  if (!vak || !sector) return null;
  // Kernmeter eerst, de rest in de volgorde van de meterlijst.
  const meters = bord.meters
    .filter((m) => m.vak === vak.code)
    .sort((a, b) => Number(b.code === vak.kernmeter) - Number(a.code === vak.kernmeter));
  const kern = meters.find((m) => m.code === vak.kernmeter);
  const kernCel = kern?.per[sector.sleutel];
  // Op een telefoon past de tabel niet: Stand, het oordeel, viel rechts buiten
  // beeld. Daar één blok per meter, met de pil vooraan (kijker 29-10).
  const smal = useSmal();

  return (
    <section aria-live="polite" data-laag2={`${sector.sleutel}:${vak.code}`}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <h2 style={{ margin: 0, fontSize: size.lead, fontWeight: weight.bold, color: color.ink }}>
          {`${sector.naam} · ${vak.naam}`}
        </h2>
        <span style={{ fontSize: 12, color: color.faint }}>
          {opGat ? `${vak.vraag} · geopend op het grootste gat` : vak.vraag}
        </span>
      </div>

      {kern && kernCel && (
        <div style={{ marginBottom: 12 }}>
          <Zin kleur={kernCel.stand}>
            <Samenvatting
              vak={vak}
              kern={kern}
              cel={kernCel}
              ict={ict ? (kern.per[ict.sleutel]?.waarde ?? null) : null}
              meters={meters}
              sector={sector.sleutel}
            />
          </Zin>
        </div>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={smal ? undefined : { overflowX: "auto" }}>
          <div style={smal ? undefined : { minWidth: 660 }}>
            {!smal && <TableHead cols={RIJ} labels={["Meter", "Nu", "ICT", "Doel", "Stand"]} />}
            {meters.map((m, i) => {
              const c = m.per[sector.sleutel];
              const ictWaarde = ict ? (m.per[ict.sleutel]?.waarde ?? null) : null;
              const doel = c?.doelTekst ?? m.doelTekst;
              const pil = c && (
                <Pil kleur={c.stand} title={c.reden ?? undefined}>
                  {c.standWoord}
                </Pil>
              );
              return (
                <button
                  key={m.code}
                  type="button"
                  data-meter={m.code}
                  onClick={() => onMeter(m.code)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: smal ? "minmax(0, 1fr)" : RIJ,
                    gap: smal ? 6 : 0,
                    alignItems: "center",
                    width: "100%",
                    textAlign: "left",
                    background: "none",
                    border: "none",
                    borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                    padding: "10px 16px",
                    font: "inherit",
                    fontSize: size.meta,
                    color: color.body,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ minWidth: 0, paddingRight: smal ? 0 : 12 }}>
                    <span style={{ ...cijfer, color: color.faint, fontSize: size.label }}>
                      {m.code}
                    </span>{" "}
                    <span style={{ color: color.ink, fontWeight: weight.medium }}>{m.naam}</span>
                    {m.code === vak.kernmeter && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: size.label,
                          color: color.soft,
                          border: `1px solid ${color.border}`,
                          borderRadius: radius.pill,
                          padding: "0 6px",
                        }}
                      >
                        kern
                      </span>
                    )}
                    <span
                      style={{
                        display: "block",
                        marginTop: 2,
                        color: color.faint,
                        fontSize: 12,
                        lineHeight: 1.4,
                      }}
                    >
                      {m.meter}
                    </span>
                  </span>
                  {smal ? (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 8,
                        fontSize: 12,
                        color: color.soft,
                      }}
                    >
                      {pil}
                      <span style={cijfer} title={c?.reden ?? undefined}>
                        <span style={{ color: color.ink, fontWeight: weight.medium }}>
                          {`nu ${getal(c?.waarde, m.eenheid)}`}
                        </span>
                        {` · ICT ${getal(ictWaarde, m.eenheid)} · doel ${doel}`}
                      </span>
                    </span>
                  ) : (
                    <>
                      <span
                        style={{ ...cijfer, color: color.ink, fontWeight: weight.medium }}
                        title={c?.reden ?? undefined}
                      >
                        {getal(c?.waarde, m.eenheid)}
                      </span>
                      <span style={{ ...cijfer, color: color.faint }}>{getal(ictWaarde, m.eenheid)}</span>
                      {/* Doel en Stand gecentreerd, zoals TableHead hun koppen zet. */}
                      <span style={{ fontSize: 12, textAlign: "center" }}>{doel}</span>
                      <span style={{ textAlign: "center" }}>{pil}</span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </Card>
    </section>
  );
}

const WOORD: Record<GezondheidsbordKleur, string> = {
  groen: "groen",
  oranje: "oranje",
  rood: "rood",
  grijs: "nog niet geteld of nog te bouwen",
};

/** "Het vakje Skills is rood: …" — de zin uit de maquette, met de woorden van de server. */
function Samenvatting({
  vak,
  kern,
  cel,
  ict,
  meters,
  sector,
}: {
  vak: Vakje;
  kern: GezondheidsbordMeter;
  cel: GezondheidsbordCel;
  ict: number | null;
  meters: readonly GezondheidsbordMeter[];
  sector: string;
}) {
  const telling = new Map<GezondheidsbordKleur, number>();
  for (const m of meters) {
    const k = m.per[sector]?.stand ?? "grijs";
    telling.set(k, (telling.get(k) ?? 0) + 1);
  }
  const delen = (["groen", "oranje", "rood", "grijs"] as const)
    .filter((k) => telling.get(k))
    .map((k) => `${telling.get(k)} ${WOORD[k]}`);
  const zin =
    cel.stand === "grijs"
      ? `Het vakje ${vak.naam} heeft nog geen kleur: ${kern.naam.toLowerCase()} is ${cel.standWoord}.`
      : `Het vakje ${vak.naam} is ${cel.stand}: ${kern.naam.toLowerCase()} staat op ${getal(
          cel.waarde,
          kern.eenheid,
        )}. ICT haalt ${getal(ict, kern.eenheid)}, het doel is ${cel.doelTekst}.`;
  return (
    <>
      <b style={{ fontWeight: weight.medium, color: color.ink }}>{zin}</b>{" "}
      {`Van de ${meters.length} meters onder dit vakje zijn er ${delen.join(", ")}.`}
    </>
  );
}
